import { decode } from 'blurhash';
// eslint-disable-next-line import/no-unresolved
import { throttle } from 'throttle-debounce';

const selector = '.responsive-image--lazy';

/**
 * Create and return a canvas which renders
 *
 * @param {string} blurhash Blurhash to be displayed
 * @returns {HTMLDivElement|false} Canvas which renders the blurhash
 */
export const BlurhashImage = (blurhash) => {
  if (!blurhash) return false;

  const container = document.createElement('div');
  const canvas = document.createElement('canvas');
  const pixels = decode(blurhash, 32, 32);
  const imageData = new ImageData(pixels, 32, 32);

  container.className = 'lazy-image__blurhash blurhash-img';
  canvas.className = 'blurhash-img__canvas';
  canvas.width = '32';
  canvas.height = '32';

  const context = canvas.getContext('2d');
  context.putImageData(imageData, 0, 0);

  container.appendChild(canvas);

  return container;
};

/**
 *  Lazyloaded Image using https://blurha.sh
 *
 * @param {HTMLElement} el
 * @return {HTMLElement}
 */
export const LazyImage = (el) => {
  // Do not proceed if this el has already been initialised
  if (el.classList.contains('lazy-image')) {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.info('Lazy image has already been initialised', {
        el,
      });
    }
    return el;
  }

  /** @type {?number} */
  let currentSize = null;

  /** @type {array} */
  const ratios = [];

  const wrapperEl = document.createElement('div');
  const imageWrapperEl = document.createElement('div');
  const fallbackEl = document.createElement('div');

  /** @type {?string} */
  const blurhash = el.dataset?.blurhash;

  /** @type {number} */
  const defaultWidth = el.dataset?.defaultWidth ?? 1;

  /** @type {number} */
  const defaultHeight = el.dataset?.defaultHeight ?? 1;

  /** @type {?array} */
  let ratiosJson = el.dataset?.ratiosJson;

  /** @type {?HTMLElement} */
  let imageContent = el.firstElementChild;

  // Do not proceed if required data is missing
  if (!ratiosJson || !blurhash || !imageContent) {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Lazy image failed to load due to missing data', {
        ratiosJson,
        blurhash,
        imageContent,
      });
    }
    return el;
  }

  // Process image template/structure which will be loaded
  if (imageContent.tagName === 'NOSCRIPT') {
    // Safari handles noscript contents differently to other browsers
    if (imageContent.firstChild instanceof Text) {
      const parsedContent = new DOMParser().parseFromString(
        imageContent.firstChild.textContent,
        'text/html'
      );
      imageContent = parsedContent.body.firstElementChild;
    } else {
      imageContent = imageContent.firstChild;
    }
  }

  const imgEl = imageContent.querySelector('img');

  // Do not proceed if img element is missing
  if (!imgEl) {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Lazy image failed to load due to missing image', {
        el,
      });
    }
    return el;
  }

  // Create blurhash element
  const blurhashImage = BlurhashImage(blurhash);
  ratiosJson = JSON.parse(ratiosJson);

  // Add intersection observe which triggers switch from
  // blurhash to image
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({ isIntersecting }) => {
      if (!isIntersecting) {
        return;
      }
      // eslint-disable-next-line no-use-before-define
      onEnter();
    });
  });

  /**
   * Get the current width of the image
   *
   * @returns {number} width in pixels
   */
  const getWidth = () => {
    if (!currentSize) {
      return defaultWidth;
    }
    return currentSize.width;
  };

  /**
   * Get the current height of the image
   *
   * @returns {number} height in pixels
   */
  const getHeight = () => {
    if (!currentSize) {
      return defaultHeight;
    }
    return currentSize.height;
  };

  /**
   * Get the style string for aspect-ratio, adjusts to width
   * if css aspect-ratio not supported
   *
   * @returns {string} style string
   */
  const getAspectRatioStyle = () => {
    if (CSS.supports('aspect-ratio', '1/1')) {
      return `aspect-ratio: ${getWidth()} / ${getHeight()};`;
    }
    return `width: ${getWidth()}px;`;
  };

  /**
   * Get the style string for aspect ratio fallback element
   *
   * @returns {string} style string
   */
  const getAspectRatioFallbackStyle = () => {
    if (CSS.supports('aspect-ratio', '1/1')) {
      return null;
    }

    return `padding-bottom: ${(getHeight() / getWidth()) * 100}%;`;
  };

  /**
   * Replace data-src, data-srcset with non 'data' versions
   */
  const processSrcs = () => {
    /* eslint-disable no-param-reassign */
    imageContent.querySelectorAll('[data-src]').forEach((elImg) => {
      elImg.src = elImg.dataset.src;
      delete elImg.dataset.src;
    });
    imageContent.querySelectorAll('[data-srcset]').forEach((elSource) => {
      elSource.srcset = elSource.dataset.srcset;
      delete elSource.dataset.srcset;
    });
    /* eslint-enable no-param-reassign */
  };

  /**
   * Set current size based on min width
   */
  const updateCurrentSize = () => {
    currentSize = ratios.find((size) => window.innerWidth >= size.minWidth);

    imageWrapperEl.style = getAspectRatioStyle();
    blurhashImage.style = getAspectRatioStyle();
    imgEl.width = getWidth();
    imgEl.height = getHeight();

    if (getAspectRatioFallbackStyle()) {
      fallbackEl.style = getAspectRatioFallbackStyle();
    }
  };

  /**
   * Add load event when the image is in view
   */
  const onLoaded = () => {
    wrapperEl.classList.add('lazy-image--loaded');
    observer.disconnect();
  };

  /**
   * Append image element when blurhash is in view
   */
  const onEnter = () => {
    imgEl.addEventListener('load', onLoaded);

    processSrcs();
  };

  /**
   * Process raw size queries into useable numbers
   */
  const processRatios = () => {
    ratiosJson.forEach((size) => {
      const { query, width, height } = size;
      const widthRegExp = /^\(min-width: (?<minWidth>\d+)px\)$/;

      let {
        groups: { minWidth },
      } = widthRegExp.exec(query);
      minWidth = Number.parseInt(minWidth, 10);
      ratios.push({ minWidth, width, height });
    });
  };

  observer.observe(wrapperEl);

  // Since we have strict height/width, if a json is provided use that
  if (ratiosJson) {
    processRatios();
    updateCurrentSize();
    window.addEventListener('resize', throttle(1000, updateCurrentSize));
  }

  if (getAspectRatioFallbackStyle()) {
    fallbackEl.className = 'lazy-image__fallback';
    wrapperEl.append(fallbackEl);
  }

  imageWrapperEl.className = 'lazy-image__img';
  imageWrapperEl.append(imageContent);
  wrapperEl.append(imageWrapperEl);

  wrapperEl.className = el.className;
  wrapperEl.classList.add('lazy-image');
  wrapperEl.prepend(blurhashImage);

  el.replaceWith(wrapperEl);

  window.addEventListener('beforeprint', () => {
    wrapperEl.classList.add('lazy-image--loaded');
    processSrcs();
  });

  return el;
};

export default function init(scope = document) {
  if (scope.querySelector(selector))
    scope.querySelectorAll(selector).forEach((el) => LazyImage(el));
}
